import { Entry } from "contentful";
import * as React from "react";
import { IFooter } from "../../contentfulTypes";
import { AppContext, IAppInterface } from "../../context";
import { getCachedQuery } from "../../util/contentfulQueries";
import { markReady } from "../../util/prerenderReady";
import {
  buildOrganizationSchema,
  buildWebsiteSchema,
} from "../../util/structuredData";
import StructuredData from "../StructuredData";

interface IProps {
  language: string;
}

interface IState {
  footerData: Entry<IFooter> | null;
}

// Sitewide Organization + WebSite JSON-LD, rendered once and always mounted
// (never lazy), so it's present in every Prerender.io snapshot regardless
// of scroll position - unlike Footer.tsx, which is wrapped in <LazyLoad>
// and may not render its children until scrolled into view.
class OrganizationSchema extends React.Component<IProps, IState> {
  public static contextType = AppContext;
  public context!: React.ContextType<typeof AppContext>;

  constructor(props: IProps) {
    super(props);
    this.state = { footerData: null };
  }

  public componentDidMount() {
    this.getData(this.props.language);
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.language !== this.props.language) {
      this.getData(this.props.language);
    }
  }

  public getData = async (language: string) => {
    const footerData = await getCachedQuery<IFooter>(
      {
        content_type: "footer",
        limit: 1,
      },
      language
    );
    const data =
      footerData && footerData.items && footerData.items.length > 0
        ? footerData.items[0]
        : null;
    if (data) {
      this.setState({ footerData: data });
    }
    // Marked ready regardless of outcome - see src/util/prerenderReady.ts.
    markReady("organizationFooter");
  };

  public render() {
    const { siteConfig }: IAppInterface = this.context;
    if (!siteConfig) {
      return null;
    }

    const origin = window.location.origin;
    const name = siteConfig.fields.metadataDefaultTitle || "Curaçao";
    const description = siteConfig.fields.metadataDefaultDescription;
    const logoUrl = `${origin}/android-chrome-512x512.png`;

    const footer = this.state.footerData;
    const sameAs = footer
      ? [
          footer.fields.facebook,
          footer.fields.twitter,
          footer.fields.instagram,
          footer.fields.youTube,
          footer.fields.tikTok,
          footer.fields.pinterest,
        ].filter((url): url is string => Boolean(url))
      : [];

    return (
      <StructuredData
        data={[
          buildOrganizationSchema({
            name,
            url: origin,
            logoUrl,
            description,
            sameAs,
          }),
          buildWebsiteSchema({ name, url: origin }),
        ]}
      />
    );
  }
}

export default OrganizationSchema;
